import { Button, Col, Form, message, Row, Typography } from "antd";
import { useState } from "react";
import { ReactComponent as ChevronLeft } from "../../assets/chevron-left.svg";
import FloatInput from "../../components/FloatInput/floatInput";
import { useForm } from "../../utils/hooks/useForm";
import { useAuth } from "../../utils/hooks/useAuth";
import { useCountDown } from "../../utils/hooks/useCountDown";
import { allowNumOnly } from "../../utils/methods/form-validations";

interface NetworkOtpStepProps {
  phoneNumber: string;
  npiNumber: number;
  onBack: () => void;
}

/**
 * In-card OTP verification step for the signup flow.
 * Same form and behavior as Pages/VerifyOTP/networkVerifyOTP.tsx, but
 * rendered inside the signup card (same page, same URL) and styled to
 * match the suv2 design. The standalone /verify-otp page still exists
 * for the other flows that navigate to it.
 */
const NetworkOtpStep = ({
  phoneNumber,
  npiNumber,
  onBack,
}: NetworkOtpStepProps) => {
  const { form } = useForm();
  const {
    handleVerifyNetworkOTP,
    handleResendNetworkOtp,
    isLoading,
    isApiLoading,
  } = useAuth(form);
  const { count, resetCount } = useCountDown(60);
  const [isResetCode, setIsResetCode] = useState(false);

  const handleResetCode = () => {
    resetCount();
    setIsResetCode(true);
    form?.submit();
  };

  const onFinish = async (values: { otp: string }) => {
    try {
      if (isResetCode) {
        await handleResendNetworkOtp(phoneNumber, npiNumber, resetCount);
        return;
      }
      await handleVerifyNetworkOTP({
        npi_number: npiNumber,
        phoneNumber: phoneNumber,
        otp: values.otp.toString(),
      });
    } catch (error) {
      message.error(`${error}`);
    } finally {
      setIsResetCode(false);
    }
  };

  return (
    <Form
      form={form}
      name="network-verify-otp"
      onFinish={onFinish}
      className="w-full flex flex-col animate-fade-in"
    >
      {/* Card header: back / title — same pattern as the signup steps */}
      <div className="relative flex items-center justify-center mb-7">
        <Button
          className="suv2-step-btn absolute left-0"
          icon={<ChevronLeft />}
          onClick={onBack}
          aria-label="Back to account details"
        />
        <Typography.Text className="suv2-card-title suv2-otp-title block font-playfair font-bold text-center">
          Verify Phone Number
        </Typography.Text>
      </div>

      <Typography.Text className="suv2-otp-subtitle block w-full text-center">
        We sent a 6-digit verification code
      </Typography.Text>

      <Row gutter={[12, 20]} wrap align={"middle"} justify={"center"}>
        <Col span={24}>
          <Form.Item
            name="otp"
            rules={[
              {
                ...(!isResetCode && {
                  required: true,
                  message: "Please enter the OTP.",
                }),
              },
              {
                pattern: /^\d{6}$/,
                message: "OTP must be a 6-digit number.",
              },
            ]}
          >
            <FloatInput
              type="text"
              placeholder="XXXXXX"
              maxLength={6}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                allowNumOnly(e, form, "otp")
              }
              controls={false}
              onPressEnter={() => form.submit()}
              label="Verification Code"
              required={true}
            />
          </Form.Item>
        </Col>
      </Row>

      {!!count ? (
        <Typography.Text className="suv2-otp-resend block w-full text-center">
          Resend Code in {count}
        </Typography.Text>
      ) : (
        <Button
          type="link"
          block
          className="suv2-otp-resend-btn"
          onClick={handleResetCode}
        >
          Resend Code
        </Button>
      )}

      <Col span={24} className="mt-3 flex justify-center items-center w-full">
        <Button
          htmlType="submit"
          className="suv2-cta w-full border-none font-semibold disabled:opacity-45"
          loading={isLoading || isApiLoading}
        >
          Verify and Continue
        </Button>
      </Col>
    </Form>
  );
};

export default NetworkOtpStep;